import React, { useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input, { Select } from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Skeleton from '../components/ui/Skeleton';
import { Table, TableCode } from '../components/ui/Table';
import './RoutingQualityMetrics.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const EVENT_LABELS = {
  OPERATION_ADDED: 'Operação adicionada',
  OPERATION_REMOVED: 'Operação removida',
  OPERATION_MOVED: 'Operação movida',
  MACHINE_CHANGED: 'Máquina alterada',
  NARRATIVE_CHANGED: 'Narrativa alterada',
  MOV_CHANGED: 'MOV alterado',
  MULTIPLE_CHANGED: 'Múltiplo alterado',
};

function rollingPeriod(days) {
  const end = new Date();
  return {
    date_from: new Date(end.getTime() - days * DAY_MS).toISOString(),
    date_to: end.toISOString(),
  };
}

function utcCalendarDate(date) {
  return date.toISOString().slice(0, 10);
}

function customPeriod(from, to) {
  if (!from || !to || from > to) return null;
  const start = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(new Date(`${to}T00:00:00.000Z`).getTime() + DAY_MS);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { date_from: start.toISOString(), date_to: end.toISOString() };
}

function formatPercent(value) {
  return Number.isFinite(value)
    ? `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}%`
    : '—';
}

function formatAverage(value) {
  return Number.isFinite(value)
    ? new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(value)
    : '—';
}

function formatCount(value) {
  return Number.isFinite(value) ? value.toLocaleString('pt-BR') : '—';
}

function formatUtc(value) {
  return new Date(value).toLocaleString('pt-BR', {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatBucketDate(value) {
  return new Date(value).toLocaleDateString('pt-BR', {
    timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatBucketRange(item) {
  const lastDay = new Date(new Date(item.bucket_end).getTime() - 1);
  return `${formatBucketDate(item.bucket_start)} – ${formatBucketDate(lastDay)}`;
}

const BUCKET_LABELS = { DAY: 'Dia', WEEK: 'Semana', MONTH: 'Mês' };

function MetricCard({ title, value, entries, onDrilldown }) {
  return (
    <Card className="cw-routing-metrics-card">
      <h2>{title}</h2>
      <p className="cw-routing-metrics-card-value">{value}</p>
      <dl>
        {entries.map(([label, count, classification]) => (
          <React.Fragment key={label}>
            <dt>{label}</dt>
            <dd>
              {formatCount(count)}
              {classification && Number.isFinite(count) && (
                <button
                  type="button"
                  className="cw-routing-metrics-drilldown-link"
                  onClick={() => onDrilldown(classification, label)}
                  aria-label={`Ver runs: ${title} — ${label}`}
                >
                  Ver
                </button>
              )}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </Card>
  );
}

function RunDrilldown({ classification, label, period, onClose }) {
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const limit = 50;

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      ...period, classification, limit: String(limit), offset: String(offset),
    });
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    setPage(null);
    fetch(`/api/roteiro/metrics/quality/runs?${params}`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!controller.signal.aborted) setPage(payload);
      })
      .catch((err) => {
        if (controller.signal.aborted || err.name === 'AbortError') return;
        console.error('[RoutingQualityMetrics] Falha ao carregar runs:', err);
        setError('Não foi possível carregar os runs desta classificação.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [classification, period, offset, retry]);

  return (
    <Modal onClose={onClose} className="cw-routing-metrics-modal">
      <div className="cw-routing-metrics-modal-header">
        <div>
          <h2 id="cw-routing-metrics-modal-title">Runs — {label}</h2>
          <p>Primeira exportação persistida na janela selecionada (UTC).</p>
        </div>
        <Button variant="secondary" onClick={onClose}>Fechar</Button>
      </div>
      {loading && <div aria-label="Carregando runs">
        <Skeleton height="18px" style={{ marginBottom: '12px' }} />
        <Skeleton height="18px" style={{ marginBottom: '12px' }} />
        <Skeleton height="18px" />
      </div>}
      {!loading && error && (
        <div className="cw-routing-metrics-note cw-routing-metrics-error" role="alert">
          {error} <Button variant="secondary" onClick={() => setRetry((value) => value + 1)}>
            Tentar novamente
          </Button>
        </div>
      )}
      {!loading && page && <>
        {page.total === 0 ? (
          <p className="cw-routing-metrics-note">Nenhum run nesta classificação no período.</p>
        ) : (
          <>
            <p className="cw-routing-metrics-modal-count">
              {formatCount(page.total)} {page.total === 1 ? 'run' : 'runs'}
            </p>
            <Table>
              <thead><tr>
                <th>Run</th><th>Primeira exportação</th><th>Eventos</th>
                <th>Revisões</th><th>Status</th><th>Ação</th>
              </tr></thead>
              <tbody>
                {page.items.map((item) => (
                  <tr key={item.routing_run_id}>
                    <td><TableCode>{item.routing_run_id}</TableCode></td>
                    <td>{formatUtc(item.first_exported_at)}</td>
                    <td>{formatCount(item.event_count)}</td>
                    <td>{formatCount(item.revision_count)}</td>
                    <td>{item.status}</td>
                    <td><a className="cw-btn cw-btn--secondary"
                      href={`/roteiro?routing_run_id=${item.routing_run_id}`}>Abrir</a></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="cw-routing-metrics-modal-pagination">
              <span>{offset + 1}–{Math.min(offset + page.items.length, page.total)} de {page.total}</span>
              <Button variant="secondary" disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}>Anterior</Button>
              <Button variant="secondary" disabled={offset + limit >= page.total}
                onClick={() => setOffset(offset + limit)}>Próxima</Button>
            </div>
          </>
        )}
        {classification.endsWith('INDETERMINATE') && (
          <p className="cw-routing-metrics-modal-caveat">
            Sem corte confiável, eventos e revisões aparecem como indisponíveis.
            Exportações de estado não salvo permanecem nesta lista.
          </p>
        )}
      </>}
    </Modal>
  );
}

function readableLimitation(value) {
  if (value.startsWith('Sem exportação persistida')) {
    return 'Runs sem exportação persistida não entram na janela analisada.';
  }
  if (value.startsWith('Eventos V1 medem')) {
    return 'Os eventos representam alterações salvas. Eles não medem aprovação industrial nem esforço de trabalho.';
  }
  return value;
}

export default function RoutingQualityMetrics() {
  const [preset, setPreset] = useState('30');
  const [customFrom, setCustomFrom] = useState(
    () => utcCalendarDate(new Date(Date.now() - 29 * DAY_MS))
  );
  const [customTo, setCustomTo] = useState(() => utcCalendarDate(new Date()));
  const [period, setPeriod] = useState(() => rollingPeriod(30));
  const [periodError, setPeriodError] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drilldown, setDrilldown] = useState(null);
  const [bucket, setBucket] = useState('WEEK');
  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState('');
  const [seriesRetry, setSeriesRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams(period);
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    setData(null);
    fetch(`/api/roteiro/metrics/quality?${params}`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!controller.signal.aborted) setData(payload);
      })
      .catch((err) => {
        if (controller.signal.aborted || err.name === 'AbortError') return;
        console.error('[RoutingQualityMetrics] Falha ao carregar métricas:', err);
        setError('Não foi possível carregar as métricas deste período.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [period]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ ...period, bucket });
    const token = localStorage.getItem('token');
    setSeriesLoading(true);
    setSeriesError('');
    setSeries(null);
    fetch(`/api/roteiro/metrics/quality/timeseries?${params}`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (!controller.signal.aborted) setSeries(payload);
      })
      .catch((err) => {
        if (controller.signal.aborted || err.name === 'AbortError') return;
        console.error('[RoutingQualityMetrics] Falha ao carregar série temporal:', err);
        setSeriesError('Não foi possível carregar a evolução deste período.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setSeriesLoading(false);
      });
    return () => controller.abort();
  }, [period, bucket, seriesRetry]);

  const selectPreset = (value) => {
    setDrilldown(null);
    setPreset(value);
    setPeriodError('');
    if (value !== 'custom') setPeriod(rollingPeriod(Number(value)));
  };

  const applyCustom = () => {
    const next = customPeriod(customFrom, customTo);
    if (!next) {
      setPeriodError('Informe datas válidas, com início anterior ou igual ao fim.');
      return;
    }
    setPeriodError('');
    setDrilldown(null);
    setPeriod(next);
  };

  const firstPass = data?.first_pass;
  const intervention = data?.intervention;
  const events = data?.events_per_run;
  const revisions = data?.revisions_to_first_export;
  const indeterminate = data?.cohort?.indeterminate ?? 0;

  return (
    <section className="cw-routing-metrics">
      <header className="cw-routing-metrics-header">
        <div>
          <h1 className="cw-page-title">Qualidade do Roteiro</h1>
          <p className="cw-page-subtitle">Alterações salvas até a primeira exportação persistida.</p>
        </div>
        <div className="cw-routing-metrics-period">
          <label className="cw-routing-metrics-field">
            Período
            <Select value={preset} onChange={(event) => selectPreset(event.target.value)}>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="custom">Personalizado</option>
            </Select>
          </label>
          {preset === 'custom' && (
            <div className="cw-routing-metrics-custom">
              <label className="cw-routing-metrics-field">
                De (UTC)
                <Input type="date" value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)} />
              </label>
              <label className="cw-routing-metrics-field">
                Até (UTC)
                <Input type="date" value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)} />
              </label>
              <Button variant="secondary" onClick={applyCustom}>Aplicar período</Button>
            </div>
          )}
        </div>
      </header>

      {periodError && <p className="cw-routing-metrics-note cw-routing-metrics-error" role="alert">
        {periodError}
      </p>}
      <p className="cw-routing-metrics-range">
        Janela UTC: {formatUtc(period.date_from)} até {formatUtc(period.date_to)} (limite exclusivo)
      </p>

      {loading && <div aria-label="Carregando métricas" className="cw-routing-metrics-grid">
        {[0, 1, 2, 3].map((key) => (
          <Card key={key}><Skeleton height="1.2em" width="55%" />
            <Skeleton height="2em" width="30%" /></Card>
        ))}
      </div>}

      {!loading && error && (
        <div className="cw-routing-metrics-note cw-routing-metrics-error" role="alert">
          <strong>Erro ao carregar.</strong> {error}
          <Button variant="secondary" onClick={() => setPeriod({ ...period })}>Tentar novamente</Button>
        </div>
      )}

      {!loading && data && <>
        {data.cohort?.first_persisted_exports === 0 && (
          <p className="cw-routing-metrics-note">Nenhuma primeira exportação persistida no período.</p>
        )}
        {indeterminate > 0 && (
          <p className="cw-routing-metrics-note">
            <strong>{formatCount(indeterminate)} {indeterminate === 1 ? 'run não pôde' : 'runs não puderam'} ser classificado{indeterminate === 1 ? '' : 's'}.</strong>
            {' '}Isso pode ocorrer com exportação de estado não salvo ou histórico sem vínculo verificável.
            {' '}Representam {formatPercent(data.cohort?.indeterminate_percent)} da coorte.
          </p>
        )}
        <div className="cw-routing-metrics-grid">
          <MetricCard title="First Pass Rate" value={formatPercent(firstPass?.percent)}
            onDrilldown={(classification, label) => setDrilldown({ classification, label })}
            entries={[
              ['Classificáveis', firstPass?.classifiable],
              ['Aprovados de primeira', firstPass?.passed, 'FIRST_PASS'],
              ['Reprovados', firstPass?.failed, 'FIRST_PASS_FAILED'],
              ['Indeterminados', firstPass?.indeterminate, 'FIRST_PASS_INDETERMINATE'],
            ]} />
          <MetricCard title="Taxa de intervenção" value={formatPercent(intervention?.percent)}
            onDrilldown={(classification, label) => setDrilldown({ classification, label })}
            entries={[
              ['Classificáveis', intervention?.classifiable],
              ['Com intervenção', intervention?.with_intervention, 'WITH_INTERVENTION'],
              ['Sem intervenção', intervention?.without_intervention, 'WITHOUT_INTERVENTION'],
              ['Indeterminados', intervention?.indeterminate, 'INTERVENTION_INDETERMINATE'],
            ]} />
          <MetricCard title="Eventos médios por run" value={formatAverage(events?.average)}
            entries={[
              ['Eventos totais', events?.total_events],
              ['Runs considerados', events?.runs],
            ]} />
          <MetricCard title="Revisões médias até primeira exportação"
            value={formatAverage(revisions?.average)}
            entries={[
              ['Revisões totais', revisions?.total_revisions],
              ['Runs considerados', revisions?.runs],
            ]} />
        </div>

        <Card className="cw-routing-metrics-section">
          <h2>Distribuição de alterações</h2>
          <ul className="cw-routing-metrics-distribution">
            {(data.event_distribution || []).map((item) => (
              <li key={item.event_type}>
                <span>{EVENT_LABELS[item.event_type] || item.event_type}</span>
                <span className="cw-routing-metrics-bar" aria-hidden="true">
                  <span style={{ width: `${Number.isFinite(item.percent) ? Math.max(0, Math.min(100, item.percent)) : 0}%` }} />
                </span>
                <span className="cw-routing-metrics-number">{formatCount(item.count)}</span>
                <span className="cw-routing-metrics-number">{formatPercent(item.percent)}</span>
              </li>
            ))}
          </ul>
        </Card>

        {data.limitations?.length > 0 && (
          <Card className="cw-routing-metrics-section">
            <h2>Sobre estas métricas</h2>
            <ul className="cw-routing-metrics-limitations">
              {data.limitations.map((item) => <li key={item}>{readableLimitation(item)}</li>)}
            </ul>
          </Card>
        )}
      </>}
      <Card className="cw-routing-metrics-section cw-routing-metrics-evolution">
        <div className="cw-routing-metrics-evolution-header">
          <div>
            <h2>Evolução</h2>
            <p>Coorte pela primeira exportação persistida, em buckets de calendário UTC.</p>
          </div>
          <label className="cw-routing-metrics-field">
            Agrupar por
            <Select value={bucket} onChange={(event) => setBucket(event.target.value)}>
              <option value="DAY">Dia</option>
              <option value="WEEK">Semana</option>
              <option value="MONTH">Mês</option>
            </Select>
          </label>
        </div>
        {seriesLoading && (
          <div aria-label="Carregando evolução">
            <Skeleton height="18px" style={{ marginBottom: '12px' }} />
            <Skeleton height="18px" style={{ marginBottom: '12px' }} />
            <Skeleton height="18px" />
          </div>
        )}
        {!seriesLoading && seriesError && (
          <div className="cw-routing-metrics-note cw-routing-metrics-error" role="alert">
            {seriesError}
            <Button variant="secondary" onClick={() => setSeriesRetry((value) => value + 1)}>
              Tentar novamente
            </Button>
          </div>
        )}
        {!seriesLoading && series && <>
          <p className="cw-routing-metrics-evolution-note">
            {BUCKET_LABELS[series.bucket] || series.bucket} em UTC ·
            {' '}— indica ausência de runs classificáveis, não 0%.
            {' '}Buckets sem exportação permanecem na série.
          </p>
          {!series.items?.length ? (
            <p className="cw-routing-metrics-note">Nenhum bucket para este período.</p>
          ) : <>
            {series.items.every((item) =>
              item.classifiable === 0 && item.indeterminate_count === 0
            ) && (
              <p className="cw-routing-metrics-note">
                Nenhuma primeira exportação persistida no período.
              </p>
            )}
            <h3>First Pass e intervenção</h3>
            <Table>
              <thead><tr>
                <th>Período UTC</th><th>Classificáveis</th>
                <th>First Pass</th><th>Intervenção</th><th>Indeterminados</th>
              </tr></thead>
              <tbody>
                {series.items.map((item) => (
                  <tr key={item.bucket_start}>
                    <td>{formatBucketRange(item)}</td>
                    <td>{formatCount(item.classifiable)}</td>
                    <td>{formatPercent(item.first_pass_rate)}
                      <span className="cw-routing-metrics-evolution-count">
                        {formatCount(item.first_pass_count)} runs
                      </span>
                    </td>
                    <td>{formatPercent(item.intervention_rate)}
                      <span className="cw-routing-metrics-evolution-count">
                        {formatCount(item.intervention_count)} runs
                      </span>
                    </td>
                    <td>{formatCount(item.indeterminate_count)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <h3>Médias até a primeira exportação</h3>
            <Table>
              <thead><tr>
                <th>Período UTC</th><th>Runs considerados</th>
                <th>Eventos/run</th><th>Revisões/run</th>
              </tr></thead>
              <tbody>
                {series.items.map((item) => (
                  <tr key={item.bucket_start}>
                    <td>{formatBucketRange(item)}</td>
                    <td>{formatCount(item.runs_considered)}</td>
                    <td>{formatAverage(item.events_per_run)}</td>
                    <td>{formatAverage(item.revisions_per_run)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>}
        </>}
      </Card>
      {drilldown && (
        <RunDrilldown
          key={drilldown.classification}
          classification={drilldown.classification}
          label={drilldown.label}
          period={period}
          onClose={() => setDrilldown(null)}
        />
      )}
    </section>
  );
}
